'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';


interface HeaderProps {
  onBookClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onBookClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Notre Histoire', href: '/#story' },
    { label: 'La Carte', href: '/#menu' },
    { label: 'Nos Points de Vente', href: '/#locations' },
    { label: 'Contact', href: '/#contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-3' : 'py-5'
      }`}
    >
      <div
        className={`mx-auto max-w-7xl px-6 flex items-center justify-between rounded-2xl transition-all duration-500 ${
          scrolled
            ? 'glass-warm shadow-lg shadow-charcoal/10 py-3 px-8'
            : 'bg-transparent'
        }`}
      >
        {/* Logo */}
        <a href="/#" className="flex items-center gap-3 group">
          <Image
            src="/assets/logo.png"
            alt="CorsAsia Logo"
            width={100}
            height={76}
            className="w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />{/* h-10 */} 
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={`text-xs uppercase tracking-widest font-medium transition-colors duration-300 hover:opacity-100 ${
                scrolled
                  ? 'text-charcoal/60 hover:text-charcoal' :'text-parchment/70 hover:text-parchment'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <button
          onClick={onBookClick}
          className="hidden lg:flex items-center gap-2 bg-corsasia text-parchment px-6 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold hover:bg-corsasia-dark transition-all duration-300 ember-pulse"
        >
          Réserver une table
        </button>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`lg:hidden w-8 h-8 flex flex-col justify-center gap-1.5 ${
            scrolled ? 'text-charcoal' : 'text-parchment'
          }`}
          aria-label="Toggle menu"
        >
          <span
            className={`block h-px bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
          />
          <span
            className={`block h-px bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}
          />
          <span
            className={`block h-px bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden mx-6 mt-2 glass-warm rounded-2xl overflow-hidden transition-all duration-500 ${
          menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm text-charcoal/70 hover:text-charcoal font-medium uppercase tracking-widest transition-colors"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => { onBookClick(); setMenuOpen(false); }}
            className="mt-2 bg-corsasia text-parchment px-6 py-3 rounded-full text-xs uppercase tracking-widest font-semibold"
          >
            Réserver une table
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;